'use client';

import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import { Button } from '../../components/button';
import { Collapsible } from '../../components/collapsible';
import { BadgeSeverity } from '../../components/badge';
import { ArrowDown01Icon, ArrowRight01Icon, Undo02Icon } from '@terra/ui-icons';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { resolveOwaspRiskBadgeSeverity } from '@terra/shared-cvss';
import type { OwaspRiskCalculatorProps, OwaspRiskFactorKey } from './types';
import { useOwaspRiskCalculator } from './hooks';
import { FactorSelector } from './components/FactorSelector';
import { useContainerMinWidthLayout } from '../useContainerMinWidthLayout';
import { OWASP_CALCULATOR_TWO_COLUMN_MIN_WIDTH_PX } from '../object-edit-page/objectEditLayoutTokens';

type FactorGroup = {
  titleKey: string;
  factors: readonly OwaspRiskFactorKey[];
};
const LIKELIHOOD_GROUPS: readonly FactorGroup[] = [
  { titleKey: 'groups.threatAgent', factors: ['SL', 'M', 'O', 'S'] },
  { titleKey: 'groups.vulnerability', factors: ['ED', 'EE', 'A', 'ID'] },
];
const IMPACT_GROUPS: readonly FactorGroup[] = [
  { titleKey: 'groups.technicalImpact', factors: ['LC', 'LI', 'LA', 'LAc'] },
  { titleKey: 'groups.businessImpact', factors: ['FD', 'RD', 'NC', 'PV'] },
];

export function OwaspRiskCalculator({
  initialScore,
  initialVector,
  onScoreChange,
  copilotChrome,
}: OwaspRiskCalculatorProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const t = useTranslations('designSystem.owaspRisk.calculator');
  const { setContainerRef, meetsMinWidth: isTwoColumnLayout } =
    useContainerMinWidthLayout(OWASP_CALCULATOR_TWO_COLUMN_MIN_WIDTH_PX);
  const {
    factors,
    calculation,
    calculatedScore,
    vectorString,
    handleFactorChange,
    resetCalculator,
  } = useOwaspRiskCalculator({
    initialScore,
    initialVector,
    onScoreChange,
  });

  useEffect(() => {
    const factorFooterCount = copilotChrome?.factorFooters
      ? Object.keys(copilotChrome.factorFooters).length
      : 0;

    if (factorFooterCount > 0) {
      setIsExpanded(true);
    }
  }, [copilotChrome?.factorFooters]);
  const renderGroupColumn = (groups: readonly FactorGroup[]) => (
    <VStack gap={4} flex={1} minW={0} w="full" alignItems="stretch">
      {groups.map((group) => (
        <VStack key={group.titleKey} gap={3} alignItems="stretch">
          <Text fontSize="xs" fontWeight="semibold" color="fg.default">
            {t(group.titleKey)}
          </Text>
          {group.factors.map((factorKey) => (
            <FactorSelector
              key={factorKey}
              title={t(`factors.${factorKey}.label`)}
              description={t(`factors.${factorKey}.description`)}
              value={factors[factorKey]}
              onChange={(value) => handleFactorChange(factorKey, value)}
              footer={copilotChrome?.factorFooters?.[factorKey]}
            />
          ))}
        </VStack>
      ))}
    </VStack>
  );

  return (
    <VStack gap={0} w="full" alignItems="stretch">
      <HStack
        gap={3}
        w="full"
        alignItems="center"
        flexWrap="wrap"
        p={2}
        bg="bg.subtle"
        borderRadius="md"
        borderWidth="1px"
        borderColor={
          copilotChrome?.isReviewing ? 'border.accent.default' : 'border.subtle'
        }
        mb={2}
      >
        <VStack gap={0} alignItems="flex-start" minW="60px">
          <Text fontSize="2xs" color="fg.muted" fontWeight="medium">
            {t('riskScore')}
          </Text>
          <Text
            fontSize="lg"
            fontWeight="bold"
            color={calculatedScore ? 'fg.default' : 'fg.muted'}
          >
            {calculatedScore || '-'}
          </Text>
          {copilotChrome?.riskScoreFooter}
        </VStack>

        <Box h="8" w="1px" bg="border.subtle" />

        <VStack gap={0} alignItems="flex-start" minW="70px">
          <Text fontSize="2xs" color="fg.muted" fontWeight="medium">
            {t('riskLevel')}
          </Text>
          {calculation ? (
            <BadgeSeverity
              severity={resolveOwaspRiskBadgeSeverity(calculation.level)}
              size="sm"
            >
              {calculation.level}
            </BadgeSeverity>
          ) : (
            <Text fontSize="xs" color="fg.muted">
              -
            </Text>
          )}
        </VStack>

        <Box h="8" w="1px" bg="border.subtle" />

        <VStack gap={0} alignItems="flex-start" flex={1} minW={0}>
          <Text fontSize="2xs" color="fg.muted" fontWeight="medium">
            {t('riskVector')}
          </Text>
          <Text
            fontSize="xs"
            color={vectorString ? 'fg.default' : 'fg.muted'}
            wordBreak="break-all"
            lineHeight="1.3"
          >
            {vectorString || t('selectFactorsBelow')}
          </Text>
          {copilotChrome?.vectorFooter}
        </VStack>
      </HStack>
      {copilotChrome?.groupAlert ? (
        <Box w="full" mb={2}>
          {copilotChrome.groupAlert}
        </Box>
      ) : null}

      <Collapsible.Root
        open={isExpanded}
        onOpenChange={({ open }) => setIsExpanded(open)}
      >
        <HStack
          justifyContent="space-between"
          alignItems="center"
          w="full"
          mb={2}
        >
          <Collapsible.Trigger asChild>
            <Button
              size="sm"
              variant="ghost"
              fontWeight="medium"
              justifyContent="flex-start"
              px={0}
              _hover={{ bg: 'transparent' }}
            >
              <HStack gap={2}>
                {isExpanded ? (
                  <ArrowDown01Icon size={14} />
                ) : (
                  <ArrowRight01Icon size={14} />
                )}
                <Text fontSize="xs" fontWeight="medium">
                  {t('title')}
                </Text>
              </HStack>
            </Button>
          </Collapsible.Trigger>

          {isExpanded && (
            <Button
              size="xs"
              variant="ghost"
              onClick={resetCalculator}
              fontSize="xs"
              color="fg.muted"
            >
              <Undo02Icon size={12} />
              <Text ml={1}>{t('resetAll')}</Text>
            </Button>
          )}
        </HStack>

        <Collapsible.Content>
          <VStack gap={4} w="full" pt={2}>
            {calculation && (
              <HStack
                gap={4}
                w="full"
                p={2}
                bg="bg.subtle"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.subtle"
              >
                <HStack gap={2} flex={1}>
                  <Text fontSize="2xs" color="fg.muted" fontWeight="medium">
                    {t('likelihood')}
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold">
                    {calculation.likelihoodScore} ({calculation.likelihoodLevel}
                    )
                  </Text>
                </HStack>
                <Box h="4" w="1px" bg="border.subtle" />
                <HStack gap={2} flex={1}>
                  <Text fontSize="2xs" color="fg.muted" fontWeight="medium">
                    {t('impact')}
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold">
                    {calculation.impactScore} ({calculation.impactLevel})
                  </Text>
                </HStack>
              </HStack>
            )}

            <Box
              ref={setContainerRef}
              w="full"
              display="flex"
              flexDirection={isTwoColumnLayout ? 'row' : 'column'}
              alignItems={isTwoColumnLayout ? 'flex-start' : 'stretch'}
              gap={6}
            >
              {renderGroupColumn(LIKELIHOOD_GROUPS)}
              {renderGroupColumn(IMPACT_GROUPS)}
            </Box>

            <Text fontSize="2xs" color="fg.muted" textAlign="center">
              {t('clickButtonsInstruction')}
            </Text>
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
    </VStack>
  );
}
